import React, { createContext, useContext, useState } from 'react';
import api from '../api';

const MarketContext = createContext();

export const MarketProvider = ({ children, user }) => {
  const [products, setProducts] = useState([]);
  const [likedProductIds, setLikedProductIds] = useState([]);
  const [hasLoadedProducts, setHasLoadedProducts] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [marketPage, setMarketPage] = useState(1);
  const [hasMoreMarket, setHasMoreMarket] = useState(true);

  // ✅ 1️⃣ Accept username
  const fetchListings = async (page = 1, username) => {
    try {
      console.log('📡 [MarketContext] Fetching listings for page:', page);

      // ✅ 2️⃣ Pass username to backend
      const res = await api.get(
        `/products/all?sort=trending&page=${page}&limit=10${username ? `&username=${username}` : ''}`
      );
      const data = res.data?.products || [];
      const hasMore = res.data?.hasMore ?? false;

      setHasMoreMarket(hasMore);

      const viewed = JSON.parse(localStorage.getItem('viewedProducts') || '{}');
      data.forEach(product => {
        if (!viewed[product._id]) {
          api.patch(`/products/${product._id}/view`);
          viewed[product._id] = true;
        }
      });
      localStorage.setItem('viewedProducts', JSON.stringify(viewed));

      console.log(`✅ [MarketContext] Received ${data.length} products from page ${page}`);

// ✅ Update likedProductIds from `userHasLiked` (always)
const likedIdsFromPage = data.filter(p => p.userHasLiked).map(p => p._id);

// If we're loading the first page (reset scenario), replace.
// Otherwise (pagination), merge uniquely.
if (page === 1) {
  setLikedProductIds(likedIdsFromPage);
} else {
  setLikedProductIds(prev => Array.from(new Set([...prev, ...likedIdsFromPage])));
}

      return data;
    } catch (err) {
      console.error('❌ MarketContext: fetchListings failed', err);
      return [];
    }
  };

  // ✅ 4️⃣ Accept username & pass to fetchListings
  const loadListings = async (reset = false, username) => {
    if (loadingProducts || (!hasMoreMarket && !reset)) return;

    console.log('🌀 [MarketContext] loadListings called | reset:', reset, '| currentPage:', marketPage);

    if (reset) {
      setProducts([]);
      setMarketPage(1);
      setHasMoreMarket(true);
    }

if (reset) {
  setLikedProductIds([]); // ensure fresh recompute from page 1
}


    setLoadingProducts(true);
    const nextPage = reset ? 1 : marketPage + 1;

    // ✅ Pass username
    const newProducts = await fetchListings(nextPage, username);

    if (newProducts.length < 10) {
      setHasMoreMarket(false);
    }

    setProducts(prev => (reset ? newProducts : [...prev, ...newProducts]));
    setMarketPage(nextPage);
    setLoadingProducts(false);
  };

  const toggleLikeProduct = async (productId) => {
    try {
      const isLiked = likedProductIds.includes(productId);
      const newLikedProductIds = isLiked
        ? likedProductIds.filter(id => id !== productId)
        : [...likedProductIds, productId];

      // Optimistically update liked products in the context
      setLikedProductIds(newLikedProductIds);

      // Fire the backend request to like/unlike
      await api.post(`/products/${productId}/like`, { username: user.username });
    } catch (err) {
      console.error('❌ Like toggle failed:', err);
    }
  };

  return (
    <MarketContext.Provider
      value={{
        products,
        likedProductIds,
        setProducts,
        setLikedProductIds,
        hasLoadedProducts,
        setHasLoadedProducts,
        loadingProducts,
        setLoadingProducts,
        marketPage,
        setMarketPage,
        hasMoreMarket,
        setHasMoreMarket,
        loadListings,
        toggleLikeProduct,
      }}
    >
      {children}
    </MarketContext.Provider>
  );
};

export const useMarketContext = () => useContext(MarketContext);
